import { useReducer } from 'react';

import { oauthIntegrations, customIntegrations, shareFileIntegrations } from 'src/shared/entities/static-integrations';
import { type IAction } from 'src/utils/ts-utilities';

enum ActionEnum {
  ACTIVATE_OAUTH_INTEGRATION = 'activate-oauth-integration',
  ACTIVATE_CUSTOM_INTEGRATION = 'activate-custom-integration',
  ACTIVATE_SHARE_FILE_INTEGRATION = 'activate-share-file-integration',
  UPDATE_CUSTOM_INTEGRATION = 'update-custom-integration',
  RESET_INTEGRATION = 'reset-integration',
  DISCONNECT_INTEGRATION = 'disconnect-integration',
  RECONNECT_OAUTH_INTEGRATION = 'reconnect-oauth-integration',
  SHOW_SETUP_ALERT = 'show-setup-alert',
  CHOOSE_PROVIDER = 'choose-provider',
}

type IActionReducer =
  | IAction<
      ActionEnum.SHOW_SETUP_ALERT,
      | {
          type: 'oauth';
          provider: (typeof oauthIntegrations)[number];
        }
      | {
          type: 'custom';
          provider: (typeof customIntegrations)[number];
        }
    >
  | IAction<ActionEnum.ACTIVATE_OAUTH_INTEGRATION, { provider: (typeof oauthIntegrations)[number] }>
  | IAction<ActionEnum.ACTIVATE_CUSTOM_INTEGRATION, { provider: (typeof customIntegrations)[number] }>
  | IAction<ActionEnum.ACTIVATE_SHARE_FILE_INTEGRATION, { provider: (typeof shareFileIntegrations)[number] }>
  | IAction<ActionEnum.UPDATE_CUSTOM_INTEGRATION, { integrationId: string }>
  | IAction<ActionEnum.DISCONNECT_INTEGRATION, { integrationId: string }>
  | IAction<
      ActionEnum.RECONNECT_OAUTH_INTEGRATION,
      { provider: (typeof oauthIntegrations)[number]; tenantId: string | null }
    >
  | IAction<ActionEnum.RESET_INTEGRATION>
  | IAction<ActionEnum.CHOOSE_PROVIDER>;

export type IState = (
  | {
      type: 'oauth';
      provider: (typeof oauthIntegrations)[number];
    }
  | {
      type: 'custom';
      provider: (typeof customIntegrations)[number];
    }
  | {
      type: 'share-file';
      provider: (typeof shareFileIntegrations)[number];
    }
  | {
      type: null;
      provider: null;
    }
) & {
  tenantId: string | null;
  isActivateOpen: boolean;
  update: {
    isOpen: boolean;
    integrationId: string | null;
  };
  disconnect: {
    isOpen: boolean;
    integrationId: string | null;
  };
  isReconnectOpen: boolean;
  isSetupAlertOpen: boolean;
  isChooseProviderOpen: boolean;
};

const initialState: IState = {
  type: null,
  provider: null,
  tenantId: null,
  isActivateOpen: false,
  isReconnectOpen: false,
  isSetupAlertOpen: false,
  isChooseProviderOpen: false,
  disconnect: {
    isOpen: false,
    integrationId: null,
  },
  update: {
    isOpen: false,
    integrationId: null,
  },
};

const reducer = (state: IState, action: IActionReducer): IState => {
  switch (action.type) {
    case ActionEnum.ACTIVATE_OAUTH_INTEGRATION:
      return {
        ...state,
        type: 'oauth',
        provider: action.payload.provider,
        isActivateOpen: true,
        isSetupAlertOpen: false,
      };
    case ActionEnum.ACTIVATE_CUSTOM_INTEGRATION:
      return {
        ...state,
        type: 'custom',
        provider: action.payload.provider,
        isActivateOpen: true,
        isSetupAlertOpen: false,
      };
    case ActionEnum.ACTIVATE_SHARE_FILE_INTEGRATION:
      return {
        ...state,
        type: 'share-file',
        provider: action.payload.provider,
        isActivateOpen: true,
        isChooseProviderOpen: false,
      };
    case ActionEnum.UPDATE_CUSTOM_INTEGRATION:
      return {
        ...state,
        update: {
          isOpen: true,
          integrationId: action.payload.integrationId,
        },
      };
    case ActionEnum.DISCONNECT_INTEGRATION:
      return {
        ...state,
        type: null,
        provider: null,
        disconnect: {
          isOpen: true,
          integrationId: action.payload.integrationId,
        },
      };
    case ActionEnum.RECONNECT_OAUTH_INTEGRATION:
      return {
        ...state,
        isReconnectOpen: true,
        type: 'oauth',
        provider: action.payload.provider,
        tenantId: action.payload.tenantId,
      };
    case ActionEnum.SHOW_SETUP_ALERT:
      return { ...state, ...action.payload, isSetupAlertOpen: true, isChooseProviderOpen: false } as IState;
    case ActionEnum.RESET_INTEGRATION:
      return initialState;
    case ActionEnum.CHOOSE_PROVIDER:
      return { ...initialState, isChooseProviderOpen: true };
    default:
      return state;
  }
};

export const useIntegrationSlice = () => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const activateOauthIntegration = (provider: (typeof oauthIntegrations)[number]) => {
    dispatch({ type: ActionEnum.ACTIVATE_OAUTH_INTEGRATION, payload: { provider } });
  };

  const activateCustomIntegration = (provider: (typeof customIntegrations)[number]) => {
    dispatch({ type: ActionEnum.ACTIVATE_CUSTOM_INTEGRATION, payload: { provider } });
  };

  const activateShareFileIntegration = (provider: (typeof shareFileIntegrations)[number]) => {
    dispatch({ type: ActionEnum.ACTIVATE_SHARE_FILE_INTEGRATION, payload: { provider } });
  };

  const updateCustomIntegration = (integrationId: string) => {
    dispatch({ type: ActionEnum.UPDATE_CUSTOM_INTEGRATION, payload: { integrationId } });
  };

  const disconnectIntegration = (integrationId: string) => {
    dispatch({ type: ActionEnum.DISCONNECT_INTEGRATION, payload: { integrationId } });
  };

  const reconnectOauthIntegration = (provider: (typeof oauthIntegrations)[number], tenantId: string | null) => {
    dispatch({ type: ActionEnum.RECONNECT_OAUTH_INTEGRATION, payload: { provider, tenantId } });
  };

  const resetIntegration = () => {
    dispatch({ type: ActionEnum.RESET_INTEGRATION, payload: {} });
  };

  const showSetupAlert = (
    payload:
      | { type: 'oauth'; provider: (typeof oauthIntegrations)[number] }
      | { type: 'custom'; provider: (typeof customIntegrations)[number] },
  ) => dispatch({ type: ActionEnum.SHOW_SETUP_ALERT, payload });

  const chooseProvider = () => dispatch({ type: ActionEnum.CHOOSE_PROVIDER, payload: {} });

  return {
    ...state,
    activateOauthIntegration,
    activateCustomIntegration,
    activateShareFileIntegration,
    updateCustomIntegration,
    disconnectIntegration,
    reconnectOauthIntegration,
    resetIntegration,
    showSetupAlert,
    chooseProvider,
  };
};
